import useFetch from "../hooks/useFetch";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface StatBlock {
    gamesStarted: number,
    gamesCompleted: number
    tutorialComplete: number
    averageMoves: number
    percentPerfect: number
}
interface Stats {
    statsByDay: Record<string, StatBlock>,
}

interface TimeSeriesAnalysisProps {
    rawData: {date:string, value: number}[]
}
const TimeSeriesAnalysis = ({rawData}:TimeSeriesAnalysisProps) => {
  // Calculate moving averages (3-day and 7-day)
  const calculateMovingAverage = (data: { date: string, value: number }[], window: number) => {
    return data.map((item, index) => {
      const start = Math.max(0, index - window + 1);
      const values = data.slice(start, index + 1).map(d => d.value);
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      return {
        date: item.date,
        value: Number(avg.toFixed(2))
      };
    });
  };

  const ma3 = calculateMovingAverage(rawData, 3);
  const ma7 = calculateMovingAverage(rawData, 7);

  // Calculate basic statistics
  const values = rawData.map(d => d.value);
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

// Exclude the first and last day from the trend analysis
const trendValues = values.slice(1, -1);
const trendN = trendValues.length;
const trendXValues = Array.from({ length: trendN }, (_, i) => i + 1); // Start from 1 to exclude the first day
const trendSumX = trendXValues.reduce((sum, x) => sum + x, 0);
const trendSumY = trendValues.reduce((sum, y) => sum + y, 0);
const trendSumXY = trendXValues.reduce((sum, x, i) => sum + x * trendValues[i], 0);
const trendSumXX = trendXValues.reduce((sum, x) => sum + x * x, 0);

const slope = (trendN * trendSumXY - trendSumX * trendSumY) / (trendN * trendSumXX - trendSumX * trendSumX);
const intercept = (trendSumY - slope * trendSumX) / trendN;

// Generate trend line data
const trendData = rawData.map((item, index) => ({
    date: item.date,
    value: index > 0 && index < rawData.length - 1 ? Number((slope * (index - 1) + intercept).toFixed(2)) : null
}));


  // Combine all data for visualization
  const chartData = rawData.map((item, index) => ({
    date: item.date.slice(5),  // Show only MM-DD
    actual: item.value,
    'MA-3': ma3[index].value,
    'MA-7': ma7[index].value,
    trend: trendData[index].value
  }));

  console.log({chartData})
  return (
    <div className="space-y-4">
    <div className="stats">
      <div className="stat">
        <div className="stat-value">{mean.toFixed(1)}</div>
        <div className="stat-label">Average Games/Day</div>
      </div>
      <div className="stat">
        <div className="stat-value">{stdDev.toFixed(1)}</div>
        <div className="stat-label">Standard Deviation</div>
      </div>
      <div className="stat">
        <div className="stat-value">{slope > 0 ? '+' : ''}{slope.toFixed(2)}</div>
        <div className="stat-label">Daily Trend</div>
      </div>
      <div className="stat">
        <div className="stat-value">{((stdDev / mean) * 100).toFixed(1)}%</div>
        <div className="stat-label">Volatility</div>
      </div>
    </div>
    <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="actual" stroke="#8884d8" dot={true}  />
            {/* <Line type="monotone" dataKey="MA-3" stroke="#82ca9d" dot={false}  />
            <Line type="monotone" dataKey="MA-7" stroke="#ffc658" dot={false} /> */}
            <Line type="monotone" dataKey="trend" stroke="#ff7300" dot={false} strokeDasharray="5 5" />
        </LineChart>
    </ResponsiveContainer>
    </div>
  );
};

export function useAllPlayersStats() {
    const { error, data: _data } = useFetch<Stats>(
        `https://www.mutatle.com/Mutatle/get_game_data.php`
    );
    if (error){
        return {
            data: null,
            loading: false,
            error: true
        }
    }
    if(!_data) return {
        data: null,
        loading: true,
        error: false
    }
    const data:Stats = _data
    const { statsByDay } = data || { statsByDay: {}, statsByIp: {} }
    
    const gamesStarted = Object.values(statsByDay).reduce((acc, { gamesStarted }) => acc + gamesStarted, 0)
    const gamesCompleted = Object.values(statsByDay).reduce((acc, { gamesCompleted }) => acc + gamesCompleted, 0)
    const tutorialComplete = Object.values(statsByDay).reduce((acc, { tutorialComplete }) => acc + tutorialComplete, 0)
    const averageMoves = Object.values(statsByDay).map(({ averageMoves }) => averageMoves).filter(x=>x)
    const averageOfAverages = averageMoves.length ? averageMoves.reduce((acc, x) => acc + x, 0) / averageMoves.length : 0

    const percentPerfects = Object.values(statsByDay).map(({ percentPerfect }) => percentPerfect).filter(x=>x)
    const averagePercentPerfect = percentPerfects.reduce((acc, x) => acc + x, 0) / Object.values(percentPerfects).length

    const gamesStartedToday = statsByDay[new Date().toISOString().split('T')[0]]?.gamesStarted || 0
    const gamesCompletedToday = statsByDay[new Date().toISOString().split('T')[0]]?.gamesCompleted || 0
    const tutorialCompleteToday = statsByDay[new Date().toISOString().split('T')[0]]?.tutorialComplete || 0
    const averageMovesToday = statsByDay[new Date().toISOString().split('T')[0]]?.averageMoves || 0
    const percentPerfectToday = statsByDay[new Date().toISOString().split('T')[0]]?.percentPerfect || 0
    return {
        data: {
            gamesStarted,
            statsByDay,
            gamesCompleted,
            tutorialComplete,
            averageOfAverages,
            averagePercentPerfect,
            gamesStartedToday,
            gamesCompletedToday,
            tutorialCompleteToday,
            averageMovesToday,
            percentPerfectToday
        },
        loading: false,
        error: false
    }
}

function MutatleStats() {
    const { data, loading, error } = useAllPlayersStats();
    if (loading) return <div>Loading...</div>
    if (error) return <div>Error...</div>
    if (!data) return <div>No data</div>
    const {
        gamesStarted,
        gamesCompleted,
        tutorialComplete,
        statsByDay,
        gamesStartedToday,
        averageOfAverages,
        gamesCompletedToday,
        tutorialCompleteToday,
        averageMovesToday,
        percentPerfectToday,
        averagePercentPerfect
    } = data

    const playersByDayGraph = Object.entries(statsByDay).map(([date, stats]) => ({
        date,
        totalPlayers: stats.gamesStarted,
    }));
    const completionRateGraph = Object.entries(statsByDay).map(([date, stats]) => ({
        date,
        completionRate: stats.gamesCompleted / stats.gamesStarted,
    }));
    return (
        <div>
            <h1>Stats</h1>
            <div className='stats'>
                <div className='stat'>
                    <div className='stat-value'>{gamesCompletedToday}</div>
                    <div className='stat-label'>Games completed today</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{gamesStartedToday}</div>
                    <div className='stat-label'>Games started today</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{tutorialCompleteToday}</div>
                    <div className='stat-label'>Tutorial completed today</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{averageMovesToday.toFixed(2)}</div>
                    <div className='stat-label'>Average moves today</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{percentPerfectToday.toFixed(2)}%</div>
                    <div className='stat-label'>Perfect rate today</div>
                </div>

                <div className='stat'>
                    <div className='stat-value'>{gamesStarted}</div>
                    <div className='stat-label'>Games started</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{gamesCompleted}</div>
                    <div className='stat-label'>Games completed</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{tutorialComplete}</div>
                    <div className='stat-label'>Tutorial completed</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{averageOfAverages.toFixed(2)}</div>
                    <div className='stat-label'>Average of average moves</div>
                </div>
                <div className='stat'>
                    <div className='stat-value'>{averagePercentPerfect.toFixed(2)}%</div>
                    <div className='stat-label'>Perfect rate</div>
                </div>


                <div className='stat'>
                    <div className='stat-value'>{(gamesCompleted / gamesStarted * 100).toFixed(2)}%</div>
                    <div className='stat-label'>Completion rate</div>
                </div>
            </div>
            <h2>Total Players per Day</h2>
            <TimeSeriesAnalysis rawData={playersByDayGraph.map(d=>({date:d.date, value:d.totalPlayers})) } />
            <h2>Completion Rate per Day</h2>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={completionRateGraph}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="completionRate" stroke="#8884d8" />
                </LineChart>
            </ResponsiveContainer>
            <h2>Average Moves per Day</h2>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={Object.entries(statsByDay).map(([date, stats]) => ({
                    date,
                    averageMoves: stats.averageMoves,
                }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="averageMoves" stroke="#8884d8" />
                </LineChart>
            </ResponsiveContainer>
            <h2>Perfect Percent by day</h2>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={Object.entries(statsByDay).map(([date, stats]) => ({
                    date,
                    percentPerfect: stats.percentPerfect,
                }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="percentPerfect" stroke="#8884d8" />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default MutatleStats;